import React, { useRef, useContext, Suspense, lazy } from 'react';
import c from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import { DuperbrowseStreamContext } from '../../../../Duperbrowse/DuperbrowseStreamContextProvider/DuperbrowseStreamContext';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ThemeOptions, ThemeOverride } from '@wix/da-react-context/pkg/ThemeContext';
import StandardThumb from '../../../../DeviationViews/Thumb/Standard';
import SwipeSlider from '../../../../SwipeSlider';
import { OverlayContextProvider } from '@wix/da-react-context/pkg/OverlayContext';

import Controls from './Controls';
import Slide from './Slide';
import { useDebouncedState, useOnResize } from './hooks';
import { SlideAnimationState, SlideDirection } from './types';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';

import s from './DeviationSlideshow.scss';

const Track = lazy(() => import('./Track'));

export const SLIDESHOW_HEIGHT = 524;
export const MOBILE_SLIDESHOW_HEIGHT = 356;
export const SLIDESHOW_DEFAULT_WIDTH = 750; // used in SSR to fit image
const SLIDESHOW_RESIZE_THROTTLE = 500;
const SLIDESHOW_SWITCH_THROTTLE = 300;

interface Props {
  items: PapiDeviation[];
  useCoverMode?: boolean;
}

const DeviationSlideshow: React.FC<Props> = ({ items, useCoverMode = false }) => {
  // Get duperbrowse context to find out if we're coming back from a deviation page.
  // If we are, we will set said deviation as the active slide on the initial state.
  const { lastViewedItem } = useContext(DuperbrowseStreamContext);
  const lastViewedIndex = lastViewedItem
    ? items.findIndex(item => item.deviationId === lastViewedItem.itemId)
    : -1;

  const initialAnimationState: SlideAnimationState = {
    index: lastViewedIndex > -1 ? lastViewedIndex : 0,
    direction: null,
  };

  // In order to avoid rendering many times on each resize event, or if the user
  // clicks prev/next too fast, we debounce updates to `containerWidth` and `animationState`
  const [containerWidth, setContainerWidth] = useDebouncedState<number | null>(
    null,
    SLIDESHOW_RESIZE_THROTTLE
  );
  const [animationState, setAnimationState] = useDebouncedState(
    initialAnimationState,
    SLIDESHOW_SWITCH_THROTTLE
  );
  const { index: currentIndex } = animationState;

  // Catch 'resize' events and trigger an update to `containerWidth` every time
  const containerRef = useRef<HTMLDivElement>(null);
  useOnResize(() => {
    const currentWidth = containerRef.current!.offsetWidth;
    setContainerWidth(currentWidth);
  });

  // The first time this component is rendered happens on server-side,
  // which means the container width will not be available.
  // We use this information to keep a flag that determines whether we're
  // currently doing SSR or not.
  // In order to make page loads faster, we only render the first item when in SSR,
  // and another re-render is triggered after page load which will load subsequent items.
  const isSSR = !containerWidth;

  const isMobile = useContext(MobileContext);

  if (items.length < 1) {
    return null;
  }

  return (
    <div className={c(['root'], 'ds-surface-secondary')} ref={containerRef}>
      <div
        className={s['frame']}
        style={{
          height: isMobile ? MOBILE_SLIDESHOW_HEIGHT : SLIDESHOW_HEIGHT,
        }}
      >
        {isSSR ? (
          <Slide
            deviation={items[0]}
            containerWidth={SLIDESHOW_DEFAULT_WIDTH}
            useCoverMode={useCoverMode}
          />
        ) : isMobile ? (
          <OverlayContextProvider value={{ isSwiperMode: true, showThumbStatusIndicators: false }}>
            <ThemeOverride theme={ThemeOptions.DARK}>
              <SwipeSlider indicatorsClassName={s['swipe-indicators']}>
                {items.map((item, index) => (
                  <StandardThumb
                    key={index}
                    deviation={item}
                    width={containerWidth!}
                    height={MOBILE_SLIDESHOW_HEIGHT}
                    responsive={true}
                  />
                ))}
              </SwipeSlider>
            </ThemeOverride>
          </OverlayContextProvider>
        ) : (
          <ErrorBoundary debugComponent="DeviationSlideshow">
            <Suspense fallback={null}>
              <Track
                items={items}
                containerWidth={containerWidth!}
                useCoverMode={useCoverMode}
                animationState={animationState}
              />
              <Controls
                currentIndex={currentIndex}
                itemCount={items.length}
                onPrevClick={() =>
                  setAnimationState({
                    index: currentIndex === 0 ? items.length - 1 : currentIndex - 1,
                    direction: SlideDirection.LEFT,
                  })
                }
                onNextClick={() =>
                  setAnimationState({
                    index: currentIndex === items.length - 1 ? 0 : currentIndex + 1,
                    direction: SlideDirection.RIGHT,
                  })
                }
              />
              )
            </Suspense>
          </ErrorBoundary>
        )}
      </div>
    </div>
  );
};

export default DeviationSlideshow;
